import { Box, Icon, theme, TransparentButton } from '@redriverpress/system'
import PropTypes from 'prop-types'
import { CircularProgressbarWithChildren } from 'react-circular-progressbar'
import styled from 'styled-components'

import ProgressBarWrapper from './ProgressBarWrapper'

export const ActionButton = styled(TransparentButton)`
  background-color: ${props => props.theme.colors[props.bg] || props.bg};
  border-radius: ${props => props.theme.radii.circular};
  border: none;
  cursor: pointer;
  transition: background 0.15s ease-in-out;
  z-index: 1;
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: ${props => props.theme.colors[props.bgHover] || props.bgHover};
  }

  &:focus {
    outline: none;
  }
`

const TYPES = {
  RECORDER: 'recorder',
  PLAYER: 'player',
}

const colorsSchemeByType = {
  [TYPES.PLAYER]: {
    background: theme.colors.green500,
    backgroundHover: theme.colors.green700,
    path: theme.colors.green500,
    trail: theme.colors.green100,
  },
  [TYPES.RECORDER]: {
    background: theme.colors.red500,
    backgroundHover: theme.colors.red700,
    path: theme.colors.red500,
    trail: theme.colors.red100,
  },
}

const getProgressBarStyle = colors => ({
  // Customize the path, i.e. the "completed progress"
  path: {
    // Path color
    stroke: colors.path,
    // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
    strokeLinecap: 'round',
    // Customize transition animation
    transition: 'stroke-dashoffset 0.5s ease 0s',
    // Rotate the path
    // transform: 'rotate(0.25turn)',
    transformOrigin: 'center center',
  },
  // Customize the circle behind the path, i.e. the "total progress"
  trail: {
    // Trail color
    stroke: colors.trail,
    // Whether to use rounded or flat corners on the ends - can use 'butt' or 'round'
    strokeLinecap: 'butt',
    // Rotate the trail
    transform: 'rotate(0.25turn)',
    transformOrigin: 'center center',
  },
})

export default function RecPlayButton({
  type,
  small,
  value,
  maxValue,
  onClick,
  children,
  icon,
  progressBarStyles,
}) {
  const colors = colorsSchemeByType[type]

  return (
    <ProgressBarWrapper>
      <Box size={small ? 38 : ['64px', null, '94px', '168px']}>
        <CircularProgressbarWithChildren
          value={value}
          strokeWidth={5}
          maxValue={maxValue}
          styles={progressBarStyles || getProgressBarStyle(colors)}
        >
          <ActionButton
            bg={colors.background}
            bgHover={colors.backgroundHover}
            position='absolute'
            onClick={onClick}
            size={small ? 30 : ['50px', null, '78px', '140px']}
          >
            {children || (
              <Icon
                color='white'
                fontSize={small ? 2 : [4, null, null, 6]}
                icon={icon}
              />
            )}
          </ActionButton>
        </CircularProgressbarWithChildren>
      </Box>
    </ProgressBarWrapper>
  )
}

RecPlayButton.displayName = 'RecPlayButton'

const progressbarTypes = {
  stroke: PropTypes.string,
  strokeLinecap: PropTypes.string,
  transition: PropTypes.string,
  transform: PropTypes.string,
  transformOrigin: PropTypes.string,
}

RecPlayButton.defaultProps = {
  type: TYPES.RECORDER,
  icon: 'microphone',
  value: 1,
  maxValue: 1,
  children: null,
  small: false,
  progressBarStyles: null,
}

RecPlayButton.propTypes = {
  type: PropTypes.oneOf([TYPES.PLAYER, TYPES.RECORDER]),
  value: PropTypes.number,
  maxValue: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
  icon: PropTypes.string,
  small: PropTypes.bool,
  progressBarStyles: PropTypes.shape({
    path: progressbarTypes,
    trail: progressbarTypes,
  }),
}
